/* eslint-disable jsx-a11y/anchor-is-valid */
import {useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {useFormik} from 'formik'
import {login as login_api, LoginParams} from '../core/_requests'
import {useAuth} from '../core/AuthContext'

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
  password: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),
})

const initialValues: LoginParams = {
  email: '',
  password: '',
}

export function Login() {
  const {login, logout} = useAuth()
  const [loading, setLoading] = useState(false)
  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      try {
        const data = await login_api({...values})
        if (data?.token_type === 'Bearer') login(data?.auth_token, data?.refresh_token)
      } catch (error: any) {
        console.error(error)
        logout()
        setStatus(error.response.data.message || 'The login detail is incorrect')
        setSubmitting(false)
      } finally {
        setLoading(false)
      }
    },
  })

  return (
    <>
      <form
        className='form w-100'
        onSubmit={formik.handleSubmit}
        noValidate
        id='kt_login_signin_form'
      >
        <div className='flex flex-col lg:flex-row h-screen w-screen overflow-hidden'>
          {/* Banner Section - 40% */}
          <div className='w-full lg:w-[40%] h-40 lg:h-full bg-gradient-to-br from-primary to-primary flex items-center justify-center'>
            <img
              src='https://res.cloudinary.com/tobilasinde/image/upload/v1753544454/schedule/asset/banner_white_guleml.png'
              alt='Banner'
              className='h-15 lg:h-25 object-contain'
            />
          </div>

          {/* Login Section - 60% */}
          <div className='w-full lg:w-[60%] flex items-center justify-center bg-[#EAF3F3]'>
            <div className='bg-white shadow-lg rounded-lg px-8 py-10 w-full max-w-md mx-4'>
              <h1 className='text-2xl font-bold text-center mb-6 text-p_black'>Sign In</h1>

              {formik.status && (
                <div className='mb-6 w-full rounded bg-red-100 border border-red-400 text-red-700 px-4 py-3 text-sm'>
                  <strong className='font-semibold'>Error:</strong> {formik.status}
                </div>
              )}

              {/* Email Input */}
              <input
                type='email'
                autoComplete='off'
                placeholder='Email'
                {...formik.getFieldProps('email')}
                className={clsx(
                  'mb-10 w-full px-4 py-3 rounded bg-gray-100 focus:outline-none focus:ring-2',
                  {
                    'focus:ring-red-500 border border-red-500':
                      formik.touched.email && formik.errors.email,
                    'focus:ring-primary border border-primary':
                      formik.touched.email && !formik.errors.email,
                  }
                )}
              />
              {formik.touched.email && formik.errors.email && (
                <div className='text-red-500 text-sm mb-4'>
                  <span role='alert'>{formik.errors.email}</span>
                </div>
              )}

              {/* Password Input */}
              <input
                type='password'
                autoComplete='off'
                placeholder='Password'
                {...formik.getFieldProps('password')}
                className={clsx(
                  'mb-10 w-full px-4 py-3 rounded bg-gray-100 focus:outline-none focus:ring-2',
                  {
                    'focus:ring-red-500 border border-red-500':
                      formik.touched.password && formik.errors.password,
                    'focus:ring-primary border border-primary':
                      formik.touched.password && !formik.errors.password,
                  }
                )}
              />
              {formik.touched.password && formik.errors.password && (
                <div className='text-red-500 text-sm mb-4'>
                  <span role='alert'>{formik.errors.password}</span>
                </div>
              )}

              <div className='text-right text-sm mb-4'>
                <a href='/auth/forgot-password' className='text-primary hover:underline'>
                  Forgot your password?
                </a>
              </div>

              <div className='text-center'>
                <button
                  type='submit'
                  id='kt_sign_in_submit'
                  className={clsx(
                    'w-full font-bold py-3 rounded transition-colors duration-200',
                    'bg-primary text-white hover:bg-teal-700',
                    {
                      'opacity-50 cursor-not-allowed': formik.isSubmitting || !formik.isValid,
                    }
                  )}
                  disabled={formik.isSubmitting || !formik.isValid}
                >
                  {!loading && <span className='inline-block'>Continue</span>}

                  {loading && (
                    <span className='flex items-center justify-center gap-2'>
                      <span>Please wait...</span>
                      <svg
                        className='w-5 h-5 animate-spin text-white'
                        xmlns='http://www.w3.org/2000/svg'
                        fill='none'
                        viewBox='0 0 24 24'
                      >
                        <circle
                          className='opacity-25'
                          cx='12'
                          cy='12'
                          r='10'
                          stroke='currentColor'
                          strokeWidth='4'
                        ></circle>
                        <path
                          className='opacity-75'
                          fill='currentColor'
                          d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z'
                        ></path>
                      </svg>
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  )
}
